import './GenerarTrazoCotizacion.scss';
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { ColorPicker } from 'primereact/colorpicker';
import { Card } from "primereact/card";
import { InputText } from "primereact/inputtext";
import { ScrollPanel } from "primereact/scrollpanel";
import { Toast } from 'primereact/toast';
import { Calendar } from 'primereact/calendar';
import { SelectButton } from 'primereact/selectbutton';
import { Polyline } from "react-leaflet";
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-routing-machine';
import 'leaflet-geometryutil';
import 'leaflet-routing-machine/dist/leaflet-routing-machine.css';
import { desEncriptar } from "../../../utilidades/encriptacion";
import Map from "../../../components/molecules/Map/Map";
import {
    getCotizacionData,
    getTrazoData,
    getTrazoIndividual,
    addRoute,
    updateRoute,
    updateRouteIndividual,
    validateHorariosPorTurno,
    formatRouteDataForApi,
    getIdaRouteReference,
    decodePolylineBase64
} from "./Integracion";
import MarkerDetailsList from "../../../components/molecules/MarkerDetailsList/MarkerDetailsList";
import RoutingMachine from "../../../components/molecules/Routing/Routing";
import AddStops from "../../../components/molecules/AddStops/AddStops";
import DraggableMarker from "../../../components/molecules/DraggableMarker/DraggableMarker";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const STEPS = [
    { id: 0, label: 'Info',     icon: 'pi pi-file-edit',    title: 'Información de la ruta' },
    { id: 1, label: 'Paradas',  icon: 'pi pi-map-marker',   title: 'Agrega las paradas' },
    { id: 2, label: 'Horarios', icon: 'pi pi-clock',        title: 'Configura horarios por turno' },
    { id: 3, label: 'Guardar',  icon: 'pi pi-check-circle', title: '¡Listo para guardar!' },
];

const quickColors = [
    { hex: '3B82F6', label: 'Azul'    },
    { hex: 'EF4444', label: 'Rojo'    },
    { hex: '22C55E', label: 'Verde'   },
    { hex: 'F59E0B', label: 'Naranja' },
    { hex: '8B5CF6', label: 'Morado'  },
    { hex: 'EC4899', label: 'Rosa'    },
    { hex: '06B6D4', label: 'Cyan'    },
    { hex: '6466f1', label: 'Índigo'  },
];

// Debe coincidir con NOMBRE_PARADA_CONTROL del backend (quote.model.ts)
const NOMBRE_PARADA_CONTROL = 'Parada de control';

const TIPOS_PARADA = [
    { label: 'Normal',     value: 'normal'  },
    { label: 'De control', value: 'control' },
];

/**
 * Renumera las paradas que conservan el nombre por defecto ("Parada N").
 * Las de control y las que el usuario renombró no se tocan.
 */
const renumerarParadas = (details) =>
    details.map((d, i) =>
        !d.paradaControl && /^Parada \d+$/.test(d.name) ? { ...d, name: `Parada ${i + 1}` } : d
    );

// ---------------------------
//  Helper: obtener usuario de sessionStorage
// ---------------------------
const obtenerUsuarioId = () => {
    try {
        const session = JSON.parse(
            desEncriptar(
                sessionStorage.getItem('dataClient'),
                process.env.REACT_APP_CLAVE_INCRIPTACION
            )
        );
        return session?.user?.ID ?? null;
    } catch {
        return null;
    }
};

const DRAFT_TTL_HORAS = 48;

// El modo va en la llave: editar un turno y editar todos usan el mismo trazoId
// pero cargan horarios distintos, así que no deben compartir borrador
const draftKey = (cotizacionId, tipoLabel, trazoId, isIndividual) => {
    const modo = !trazoId ? 'nuevo' : isIndividual ? 'individual' : 'general';
    return `trazo-draft-${cotizacionId}-${tipoLabel}-${modo}-${trazoId || 'nuevo'}`;
};

// Firma de las estructuras con las que se capturó el borrador; si cambian
// (ej. se agregó o quitó un turno) el borrador ya no corresponde
const firmaEstructuras = (estructuras) =>
    estructuras.map(e => e.id).sort((a, b) => a - b).join(',');

const guardarBorrador = (key, data) => {
    try {
        localStorage.setItem(key, JSON.stringify({ ...data, savedAt: Date.now() }));
    } catch {}
};

const cargarBorrador = (key) => {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if ((Date.now() - parsed.savedAt) / 3600000 > DRAFT_TTL_HORAS) {
            localStorage.removeItem(key);
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
};

const borrarBorrador = (key) => {
    try { localStorage.removeItem(key); } catch {}
};

const convertUnits = (data) => ({
    totalDistance: (data.totalDistance / 1000).toFixed(1),
    totalTime:     Math.round(data.totalTime / 60),
});

// Busca la estructura de un horario por estructuraId; solo usa turnoId como respaldo
// cuando el horario no trae estructuraId y el turno pertenece a una única estructura
// (ej. S2 Medio y S2 Sencillo comparten turno). Si trae un estructuraId que no está
// en la lista no se reasigna a otra estructura del mismo turno.
const buscarEstructura = (estructuras, horario) => {
    if (horario?.estructuraId != null) {
        return estructuras.find(e => e.id === horario.estructuraId) ?? null;
    }
    const porTurno = estructuras.filter(e => e.turnoId === horario?.turnoId);
    return porTurno.length === 1 ? porTurno[0] : null;
};

// Deja en una parada exactamente un horario por cada estructura disponible:
// conserva los que ya tiene, agrega vacíos los que faltan (ej. turno agregado
// después de crear el trazo) y descarta los de estructuras que no se editan aquí
const normalizarHorarios = (horariosPorTurno = [], estructuras) =>
    estructuras.map(e => {
        const existente = horariosPorTurno.find(h => h.estructuraId === e.id);
        return {
            estructuraId: e.id,
            turnoId:      e.turnoId,
            nombreTurno:  e.nombreTurno,
            horario:      existente?.horario ?? '',
        };
    });

// Muestra el turno junto con su tipo (Sencillo/Medio) para no confundir
// estructuras que comparten el mismo turno (ej. dos "E1")
const nombreTurnoConTipo = (estructura) => {
    if (!estructura?.nombreTurno) return '';
    return estructura.nombreTipo
        ? `${estructura.nombreTurno} · ${estructura.nombreTipo}`
        : estructura.nombreTurno;
};

// ---------------------------
//  Detectar modo de operación desde la URL
//  /trazo/editar/   → individual (singular)
//  /trazos/editar/  → editar todos (plural)
//  /trazos/nuevo/   → crear nuevo
// ---------------------------
const useEditorMode = (trazoId) => {
    const { pathname } = useLocation();

    const isEditing    = !!trazoId;
    const isIndividual = pathname.includes('/trazo/editar/'); // singular

    return { isEditing, isIndividual };
};

// ═════════════════════════════════════════════
//  COMPONENTE PRINCIPAL
// ═════════════════════════════════════════════
const GenerarTrazoCotizacion = () => {
    const { id: cotizacionId, tipoLabel, trazoId } = useParams();
    const navigate = useNavigate();

    // ---- Modo de operación -----------------
    const { isEditing, isIndividual } = useEditorMode(trazoId);

    // ---- Datos de cotización y estructuras ---
    const [cotizacionData,    setCotizacionData]    = useState(null);
    const [loadingCotizacion, setLoadingCotizacion] = useState(true);
    const [errorCarga,        setErrorCarga]        = useState(null);
    const [routeLabel,        setRouteLabel]        = useState('');

    /**
     * Estructuras disponibles para el paso de horarios.
     * General → todas las de la cotización.
     * Individual → solo la del trazo editado.
     */
    const [availableEstructuras, setAvailableEstructuras] = useState([]);

    // ---- Estado del mapa ------------
    const [colorHEX, setColorHEX] = useState('6466f1');
    const [assignedColor, setAssignedColor] = useState('6466f1');
    const [isAddingStop, setIsAddingStop] = useState(false);
    // Tipo con el que se crean las paradas nuevas al hacer clic en el mapa
    const [tipoNuevaParada, setTipoNuevaParada] = useState('normal');
    const [stopMarkers, setStopMarkers] = useState([]);
    const [markerDetails, setMarkerDetails] = useState([]);
    const [isReplayDisabled, setIsReplayDisabled] = useState(true);
    const [isRestoreDisabled, setIsRestoreDisabled] = useState(true);
    const [history, setHistory] = useState([]);
    const [historyMarkerDetails, setHistoryMarkerDetails] = useState([]);
    const [future, setFuture] = useState([]);   // stack para "redo"
    const [futureMarkerDetails, setFutureMarkerDetails] = useState([]);
    const [routeName, setRouteName] = useState('');
    const [routePolyline, setRoutePolyline] = useState([]);
    const [polyline, setPolyline] = useState([]);
    const [summary, setSummary] = useState({});
    const [loading, setLoading] = useState(false);
    // Una vez guardado no se vuelve a habilitar el botón (evita trazos duplicados por doble clic)
    const [guardado, setGuardado] = useState(false);
    const guardandoRef = useRef(false);
    const toast = useRef(null);
    // Se recalcula en cada render por si cambian los parámetros de la URL sin desmontar
    const draftKeyRef = useRef(null);
    draftKeyRef.current = draftKey(cotizacionId, tipoLabel, trazoId, isIndividual);
    const draftTimeoutRef = useRef(null);
    // Estado tal como se cargó; solo se guarda borrador cuando hay cambios respecto a él
    const estadoInicialRef = useRef(null);

    const isEntradaRoute = tipoLabel?.toLowerCase() === 'entrada';

    // Extrae "08:00" de "1970-01-01T08:00:00.000Z"
    const timeFromISO = (isoString) => isoString?.split('T')[1]?.substring(0, 5) ?? '';


    // ---- Ruta de regreso ------------
    const isReturnRoute = tipoLabel?.toLowerCase() === 'salida';
    const [idaPolylineCoords, setIdaPolylineCoords] = useState([]);
    const [referenceLoaded, setReferenceLoaded] = useState(false);


    // ---- Wizard -------------------
    const [currentStep, setCurrentStep] = useState(0);
    const [panelCollapsed, setPanelCollapsed] = useState(false);

    // ---------------------------
    //  INICIALIZACIÓN — carga de datos
    // ---------------------------
    useEffect(() => {
        if (!cotizacionId) return;

        const initialize = async () => {
            try {
                setLoadingCotizacion(true);

                // 1. Datos generales de la cotización (siempre se necesitan)
                const cotResponse = await getCotizacionData(cotizacionId, tipoLabel);
                if (!cotResponse.success) throw new Error('No se pudo cargar la cotización');

                const cotData = cotResponse.data;
                setCotizacionData(cotData);
                setRouteName(cotData.nombreRuta);

                // 2. Cargar trazo existente si estamos en modo edición
                if (isEditing && trazoId) {
                    await cargarTrazoExistente(trazoId, isIndividual, cotData.estructuras);
                } else {
                    // Modo nuevo → todas las estructuras disponibles
                    setAvailableEstructuras(cotData.estructuras);
                }

            } catch (error) {
                console.error('Error inicializando componente:', error);
                // Se muestra cuando termina la carga: durante la pantalla de carga el Toast no está montado
                setErrorCarga({
                    // 409: los turnos tienen paradas distintas y no se pueden editar juntos
                    bloqueado: error.response?.status === 409,
                    mensaje:   error.response?.data?.message ?? 'Error cargando datos de la cotización',
                });
            } finally {
                setLoadingCotizacion(false);
            }
        };

        initialize();
    }, [cotizacionId, trazoId, isEditing, isIndividual]);

    useEffect(() => {
        if (loadingCotizacion || !errorCarga) return;

        toast.current?.show({
            severity: errorCarga.bloqueado ? 'warn' : 'error',
            summary:  errorCarga.bloqueado ? 'No se pueden editar todos los turnos juntos' : 'Error',
            detail:   errorCarga.mensaje,
            life:     errorCarga.bloqueado ? 6000 : 3000,
        });

        if (!errorCarga.bloqueado) return;
        // Regresar al detalle para editar cada turno por separado
        const t = setTimeout(() => navigate(`/cotizaciones/empresariales/detalle/${cotizacionId}`), 6000);
        return () => clearTimeout(t);
    }, [loadingCotizacion, errorCarga]);


    useEffect(() => {
        if (isEditing) return;
        if (currentStep !== 2 || !availableEstructuras.length || markerDetails.length === 0) return;

        const isSalidaRoute = tipoLabel?.toLowerCase() === 'salida';
        if (!isEntradaRoute && !isSalidaRoute) return;

        const targetIndex = isEntradaRoute ? markerDetails.length - 1 : 0;

        setMarkerDetails(prev => prev.map((detail, i) => ({
            ...detail,
            horariosPorTurno: detail.horariosPorTurno.map(h => {
                const estructura = availableEstructuras.find(e => e.id === h.estructuraId);

                const horarioAuto = isEntradaRoute
                    ? timeFromISO(estructura?.horarioFin)
                    : timeFromISO(estructura?.horarioInicio);

                if (!horarioAuto) return h;

                if (i === targetIndex) return { ...h, horario: horarioAuto };
                if (h.horario === horarioAuto) return { ...h, horario: '' };

                return h;
            }),
        })));
    }, [currentStep]);

    /**
     * Carga los datos del trazo según el modo:
     * - Individual: `getTrazoIndividual` → solo su estructura
     * - General: `getTrazoData` → todas las estructuras
     */
    const cargarTrazoExistente = async (id, individual, todasLasEstructuras) => {
        const response = individual
            ? await getTrazoIndividual(id)
            : await getTrazoData(id);

        if (!response.success) throw new Error('No se pudo cargar el trazo');

        const trazoData = response.data;

        setRouteName(trazoData.nombreRuta);
        setAssignedColor(trazoData.color);
        setColorHEX(trazoData.color);
        setRouteLabel(trazoData.tipoLabel ?? '');

        // ---- Estructuras disponibles para el paso de horarios ----
        // Se resuelven antes que las paradas para normalizar sus horarios
        let estructurasDisponibles;
        if (individual) {
            /**
             * En modo individual el trazo solo pertenece a una estructura.
             * La derivamos desde el backend; si no viene, usamos la primera parada.
             */
            const estructuraDelTrazo = trazoData.estructura
                ?? derivarEstructuraDesdeParadas(trazoData, todasLasEstructuras);

            estructurasDisponibles = estructuraDelTrazo ? [estructuraDelTrazo] : [];
        } else {
            // Modo general → todas las estructuras de la cotización
            estructurasDisponibles = todasLasEstructuras;
        }
        setAvailableEstructuras(estructurasDisponibles);

        // Paradas
        setStopMarkers(
            trazoData.paradas.map(p => ({ lat: parseFloat(p.lat), lng: parseFloat(p.lng) }))
        );
        setMarkerDetails(
            trazoData.paradas.map(p => ({
                name:      p.paradaControl ? NOMBRE_PARADA_CONTROL : p.nombre,
                lat:       p.lat,
                lng:       p.lng,
                reference: p.referencia,
                paradaControl: !!p.paradaControl,
                horariosPorTurno: normalizarHorarios(
                    p.horariosPorTurno.map(h => ({
                        // Buscar por estructuraId: puede haber varias estructuras con el mismo turno
                        estructuraId: buscarEstructura(estructurasDisponibles, h)?.id ?? null,
                        turnoId:      h.turnoId,
                        horario:      h.horario,
                    })),
                    estructurasDisponibles
                ),
            }))
        );

        // La ruta se recalcula automáticamente a partir de las paradas cargadas
        // arriba (stopMarkers). Si hay un resumen guardado lo mostramos de
        // entrada mientras se recalcula.
        if (trazoData.summary) setSummary(trazoData.summary);
    };

    /**
     * Fallback: si el backend no devuelve `estructura` en el trazo individual,
     * buscamos la estructura del primer horario. Si no está entre las estructuras
     * de la cotización (ej. estructura inactiva), se arma con los datos del trazo
     * para no perder la edición.
     */
    const derivarEstructuraDesdeParadas = (trazoData, todasLasEstructuras) => {
        const primerHorario = trazoData.paradas?.[0]?.horariosPorTurno?.[0];
        if (!primerHorario) return null;
        return buscarEstructura(todasLasEstructuras, primerHorario) ?? {
            id:          primerHorario.estructuraId,
            turnoId:     primerHorario.turnoId,
            nombreTurno: trazoData.nombreTurno,
            nombreTipo:  trazoData.nombreTipo,
        };
    };

    // ---------------------------
    //  Ruta de referencia (solo aplica a regreso)
    // ---------------------------
    useEffect(() => {
        if (!isReturnRoute || referenceLoaded || isEditing) return;

        const loadIdaReference = async () => {
            try {
                const response = await getIdaRouteReference(cotizacionId);
                if (!response.success) return;

                const { polyline } = response.data;

                if (polyline) setIdaPolylineCoords(decodePolylineBase64(polyline));

                setReferenceLoaded(true);
            } catch (error) {
                console.error('Error cargando referencia de ida:', error);
            }
        };

        loadIdaReference();
    }, [cotizacionId, isReturnRoute, referenceLoaded]);

    // ---------------------------
    //  Efectos secundarios
    // ---------------------------
    useEffect(() => {
        setIsReplayDisabled(history.length === 0);
        setIsRestoreDisabled(future.length === 0);
    }, [history, future]);

    // Sincronizar coordenadas de markers con markerDetails
    /*
        useEffect(() => {
            setMarkerDetails(prevDetails =>
                stopMarkers.map((stopMarker, index) => {
                    const existing = prevDetails[index];
                    if (existing) {
                        return { ...existing, lat: stopMarker.lat.toString(), lng: stopMarker.lng.toString() };
                    }

                    // Nueva parada: inicializar horariosPorTurno según las estructuras disponibles
                    const horariosPorTurno = availableEstructuras.map(e => ({
                        estructuraId: e.id,
                        turnoId:      e.turnoId,
                        nombreTurno:  e.nombreTurno,
                        horario:      '',
                    }));

                    return {
                        name:  `Parada ${index + 1}`,
                        lat:   stopMarker.lat.toString(),
                        lng:   stopMarker.lng.toString(),
                        reference: '',
                        horariosPorTurno,
                    };
                })
            );
        }, [stopMarkers, availableEstructuras]);
    */

    useEffect(() => { setIsAddingStop(currentStep === 1); }, [currentStep]);

    useEffect(() => {
        // no pisar el borrador mientras carga datos del server ni si la carga falló
        if (loadingCotizacion || guardado || errorCarga) return;

        // El paso actual no cuenta como cambio: solo navegar no debe crear borrador
        const contenido = JSON.stringify({ routeName, assignedColor, stopMarkers, markerDetails });

        // Primera pasada tras la carga: es el estado del servidor, no un cambio del usuario
        if (estadoInicialRef.current === null) {
            estadoInicialRef.current = contenido;
            return;
        }
        if (contenido === estadoInicialRef.current) return;

        if (draftTimeoutRef.current) clearTimeout(draftTimeoutRef.current);
        draftTimeoutRef.current = setTimeout(() => {
            guardarBorrador(draftKeyRef.current, {
                routeName, assignedColor, colorHEX,
                stopMarkers, markerDetails, currentStep,
                estructuras: firmaEstructuras(availableEstructuras),
            });
        }, 1000);

        return () => clearTimeout(draftTimeoutRef.current);
    }, [routeName, assignedColor, colorHEX, stopMarkers, markerDetails, currentStep, loadingCotizacion, guardado, availableEstructuras, errorCarga]);

    useEffect(() => {
        if (loadingCotizacion || errorCarga) return;

        const borrador = cargarBorrador(draftKeyRef.current);
        if (!borrador) return;

        // Si los turnos de la cotización cambiaron desde que se capturó el borrador,
        // sus horarios apuntan a estructuras que ya no corresponden: se descarta
        if (borrador.estructuras !== firmaEstructuras(availableEstructuras)) {
            borrarBorrador(draftKeyRef.current);
            return;
        }

        toast.current?.show({
            severity: 'warn',
            sticky: true,
            summary: 'Tienes cambios sin guardar',
            detail: 'Se encontró un borrador de esta ruta. ¿Deseas continuar donde lo dejaste?',
            content: (
                <div className="flex flex-column gap-2 w-full">
                    <span>Se encontró un borrador de esta ruta.</span>
                    <div className="flex gap-2">
                        <Button
                            label="Restaurar"
                            size="small"
                            onClick={() => {
                                setRouteName(borrador.routeName);
                                setAssignedColor(borrador.assignedColor);
                                setColorHEX(borrador.colorHEX);
                                setStopMarkers(borrador.stopMarkers);
                                setMarkerDetails(borrador.markerDetails);
                                setCurrentStep(borrador.currentStep);
                                toast.current?.clear();
                            }}
                        />
                        <Button
                            label="Descartar"
                            size="small"
                            severity="secondary"
                            onClick={() => {
                                borrarBorrador(draftKeyRef.current);
                                toast.current?.clear();
                            }}
                        />
                    </div>
                </div>
            ),
        });
    }, [loadingCotizacion, errorCarga]);

    // ---------------------------
    //  Ruta calculada a partir de las paradas
    //  La ruta se traza solo con las paradas; no hay puntos intermedios extra.
    // ---------------------------
    const handleRouteFound = useCallback((coordinates, routeSummary) => {
        setPolyline(coordinates);
        setRoutePolyline(coordinates);
        setSummary(convertUnits({
            totalDistance: routeSummary.distance,
            totalTime: routeSummary.duration,
        }));
    }, []);

    // ---------------------------
    //  Validación y navegación del wizard
    // ---------------------------
    const canAdvanceToNextStep = useCallback(() => {
        switch (currentStep) {
            case 0: return routeName.trim().length > 0;
            case 1: return stopMarkers.length >= 2;
            case 2: {
                if (!availableEstructuras.length) return false;
                const { isValid } = validateHorariosPorTurno(markerDetails, availableEstructuras);
                return isValid;
            }
            case 3: return true;
            default: return false;
        }
    }, [currentStep, routeName, stopMarkers, markerDetails, availableEstructuras]);

    const handleNextStep = () => {
        if (canAdvanceToNextStep()) {
            setCurrentStep(prev => Math.min(prev + 1, STEPS.length - 1));
            return;
        }

        const errorMessages = {
            0: 'Por favor ingresa un nombre para la ruta',
            1: 'Debes agregar al menos 2 paradas',
            2: validateHorariosPorTurno(markerDetails, availableEstructuras).errors.join(', '),
        };

        toast.current?.show({
            severity: 'warn',
            summary:  'Paso incompleto',
            detail:   errorMessages[currentStep] ?? '',
            life:     3000,
        });
    };

    const handlePrevStep = () => setCurrentStep(prev => Math.max(prev - 1, 0));

    // ---------------------------
    //  Manejo de paradas (undo/redo)
    // ---------------------------

    const pushHistory = (markers, details) => {
        setHistory(h => [...h, markers]);
        setHistoryMarkerDetails(h => [...h, details]);
        setFuture([]);          // nueva acción limpia el redo
        setFutureMarkerDetails([]);
    };

    const back = () => {
        if (!isAddingStop || history.length === 0) return;
        const prevMarkers = history.at(-1);
        const prevDetails = historyMarkerDetails.at(-1);

        setFuture(f => [...f, stopMarkers]);
        setFutureMarkerDetails(f => [...f, markerDetails]);
        setStopMarkers(prevMarkers);
        setMarkerDetails(prevDetails);
        setHistory(h => h.slice(0, -1));
        setHistoryMarkerDetails(h => h.slice(0, -1));
    };

    const restore = () => {
        if (!future.length) return;
        const nextMarkers = future.at(-1);
        const nextDetails = futureMarkerDetails.at(-1);

        setHistory(h => [...h, stopMarkers]);
        setHistoryMarkerDetails(h => [...h, markerDetails]);
        setStopMarkers(nextMarkers);
        setMarkerDetails(nextDetails);
        setFuture(f => f.slice(0, -1));
        setFutureMarkerDetails(f => f.slice(0, -1));
    };

    const removeStop = (index) => {
        pushHistory(stopMarkers, markerDetails);  // snapshot antes de eliminar
        setStopMarkers(sm => sm.filter((_, i) => i !== index));
        setMarkerDetails(md => md.filter((_, i) => i !== index));
    };

    const reverseStops = () => {
        if (stopMarkers.length < 2) return;

        pushHistory(stopMarkers, markerDetails);  // snapshot antes de invertir
        setStopMarkers(sm => [...sm].reverse());
        setMarkerDetails(md => renumerarParadas([...md].reverse()));
    };

    const toggleParadaControl = (index) => {
        pushHistory(stopMarkers, markerDetails);
        setMarkerDetails(md => renumerarParadas(md.map((d, i) => {
            if (i !== index) return d;
            return d.paradaControl
                ? { ...d, paradaControl: false, name: `Parada ${i + 1}` }
                : { ...d, paradaControl: true,  name: NOMBRE_PARADA_CONTROL };
        })));
    };

    // Las paradas y sus detalles se actualizan por separado pero con el mismo índice.
    // No se llama a otros setState dentro de un updater: React puede ejecutar los
    // updaters dos veces (StrictMode) y la parada se duplicaría en markerDetails,
    // desfasando nombres y horarios hacia la parada siguiente.
    const handleAddStop = useCallback((updaterFn) => {
        const prev = stopMarkers;
        const next = typeof updaterFn === 'function' ? updaterFn(prev) : updaterFn;

        if (next.length <= prev.length) {
            setStopMarkers(next);
            return;
        }

        pushHistory(prev, markerDetails);

        // Encontrar qué índice se insertó comparando prev vs next
        let insertIndex = next.length - 1; // fallback: al final
        for (let i = 0; i < next.length; i++) {
            if (!prev[i] || prev[i].lat !== next[i].lat || prev[i].lng !== next[i].lng) {
                insertIndex = i;
                break;
            }
        }

        const newStop = next[insertIndex];
        const esControl = tipoNuevaParada === 'control';
        const updated = [...markerDetails];
        updated.splice(insertIndex, 0, {
            name:      esControl ? NOMBRE_PARADA_CONTROL : `Parada ${insertIndex + 1}`,
            lat:       newStop.lat.toString(),
            lng:       newStop.lng.toString(),
            reference: '',
            paradaControl: esControl,
            horariosPorTurno: normalizarHorarios([], availableEstructuras),
        });

        setStopMarkers(next);
        // Renombrar las paradas que tengan nombre por defecto después del insertIndex
        setMarkerDetails(renumerarParadas(updated));
    }, [stopMarkers, markerDetails, availableEstructuras, tipoNuevaParada]);

    const handleMarkerDrag = useCallback((index, newPos) => {
        setMarkerDetails(prev => prev.map((d, i) =>
            i === index
                ? { ...d, lat: newPos.lat.toString(), lng: newPos.lng.toString() }
                : d
        ));
    }, []);

    // ---------------------------
    //  Color
    // ---------------------------
    const changeQuickColor = (hex) => {
        setColorHEX(hex);
        setAssignedColor(hex);
    };

    // ---------------------------
    //  GUARDAR — bifurcación según modo
    // ---------------------------
    const saveRoute = async () => {
        // El ref cubre clics seguidos antes de que React vuelva a renderizar
        if (guardandoRef.current || guardado) return;
        guardandoRef.current = true;

        try {
            setLoading(true);

            const points = routePolyline.map(p => ({
                lat: parseFloat(p.lat),
                lng: parseFloat(p.lng),
            }));

            const jsonString  = JSON.stringify({
                geometry:   { coordinates: points },
                properties: { strokeColor: `#${assignedColor}` },
            });
            const base64String = btoa(jsonString);
            const usuarioId    = obtenerUsuarioId();

            const routeData = {
                usuarioId,
                routeName,
                assignedColor,
                markerDetails,
                stopMarkers,
                polyline: base64String,
                summary,
            };

            const formattedData = formatRouteDataForApi(
                routeData,
                cotizacionId,
                tipoLabel,
                cotizacionData?.cliente.id
            );

            let response;

            if (!isEditing) {
                // ---- Crear nuevo ---------------
                response = await addRoute(formattedData);

            } else if (isIndividual) {
                // ---- Editar trazo individual ---------
                // Solo afecta este trazo; no toca otras estructuras
                formattedData.id = trazoId;
                response = await updateRouteIndividual(formattedData);

            } else {
                // ---- Editar trazo general (todos los turnos) ----
                formattedData.id = trazoId;
                response = await updateRoute(formattedData);
            }

            if (response.success) {
                setGuardado(true);
                borrarBorrador(draftKeyRef.current);
                toast.current?.show({
                    severity: 'success',
                    summary:  'Éxito',
                    detail:   response.message,
                    life:     3000,
                });
                setTimeout(() => {
                    navigate(`/cotizaciones/empresariales/detalle/${cotizacionId}`);
                }, 1500);
            }
        } catch (error) {
            console.error('Error guardando ruta:', error);
            toast.current?.show({
                severity: 'error',
                summary:  'Error',
                detail:   error.response?.data?.message || error.message || 'Error al guardar la ruta',
                life:     3000,
            });
        } finally {
            guardandoRef.current = false;
            setLoading(false);
        }
    };

    // ---------------------------
    //  Helpers de renderizado
    // ---------------------------
    const tipoDisplay = tipoLabel
        ? tipoLabel.charAt(0).toUpperCase() + tipoLabel.slice(1)
        : routeLabel.toUpperCase();

    const getStepHint = () => {
        switch (currentStep) {
            case 0: return `Ruta tipo: ${tipoDisplay}`;
            case 1: return isReturnRoute
                ? 'La ruta de ida se muestra en gris. Ajusta las paradas donde cambien de calle'
                : 'Haz clic en el mapa para agregar paradas';
            case 2: return isIndividual
                ? `Configurando horarios del turno: ${nombreTurnoConTipo(availableEstructuras[0])}`
                : `Configura horarios para ${availableEstructuras.length} turnos`;
            case 3: return 'Todo listo para guardar tu ruta';
            default: return '';
        }
    };

    // ---------------------------
    //  RENDER: Step 0 — Información
    // ---------------------------
    const renderInfoStep = () => (
        <div className="wizard-step-content">
            <Card className="wizard-field" style={{ marginBottom: '20px', padding: '16px' }}>
                <h4 style={{ margin: '0 0 12px', color: '#0078D4' }}>Información de la Cotización</h4>
                <div style={{ fontSize: '0.9rem', lineHeight: '1.6' }}>
                    <p><strong>Cliente:</strong> {cotizacionData?.cliente.nombre}</p>
                    <p><strong>Tipo de ruta:</strong> {tipoDisplay}</p>
                    <p><strong>Turnos configurados:</strong> {cotizacionData?.estructuras.length}</p>
                    {isIndividual && (
                        <p style={{ color: '#F59E0B', fontWeight: 600 }}>
                            <i className="pi pi-info-circle" style={{ marginRight: 6 }} />
                            Editando turno individual — los cambios no afectan otros turnos
                        </p>
                    )}
                </div>
            </Card>

            <div className="wizard-field">
                <label className="wizard-label">
                    <i className="pi pi-map" />
                    Nombre de la ruta
                </label>
                <InputText
                    value={routeName}
                    onChange={(e) => setRouteName(e.target.value)}
                    placeholder="Ej: Ruta Principal ABC"
                    className="wizard-input"
                />
            </div>

            <div className="wizard-field">
                <label className="wizard-label">
                    <i className="pi pi-palette" />
                    Color de la ruta
                </label>
                <div className="quick-colors">
                    {quickColors.map(color => (
                        <button
                            key={color.hex}
                            className={`quick-color-btn ${assignedColor === color.hex ? 'selected' : ''}`}
                            style={{ backgroundColor: `#${color.hex}` }}
                            onClick={() => changeQuickColor(color.hex)}
                            title={color.label}
                        />
                    ))}
                </div>
                <div className="custom-color-row">
                    <ColorPicker
                        value={colorHEX}
                        onChange={(e) => { setColorHEX(e.value); setAssignedColor(e.value); }}
                    />
                    <span style={{ fontSize: '0.9rem', color: '#64748b' }}>#{colorHEX}</span>
                </div>
            </div>
        </div>
    );

    // ---------------------------
    //  RENDER: Step 1 — Paradas
    // ---------------------------
    const renderParadasStep = () => (
        <div className="wizard-step-content">
            <div className="wizard-instruction-card">
                <div className="instruction-icon"><i className="pi pi-info-circle" /></div>
                <div className="instruction-text">
                    Haz clic en el mapa para agregar paradas. Necesitas al menos 2 para continuar.
                </div>
            </div>

            <div className="wizard-field">
                <label className="wizard-label">
                    <i className="pi pi-flag" />
                    Tipo de parada a agregar
                </label>
                <SelectButton
                    value={tipoNuevaParada}
                    options={TIPOS_PARADA}
                    onChange={(e) => { if (e.value) setTipoNuevaParada(e.value); }}
                />
                {tipoNuevaParada === 'control' && (
                    <small style={{ display: 'block', marginTop: 6, color: '#B45309' }}>
                        Las paradas de control se nombran "{NOMBRE_PARADA_CONTROL}" automáticamente.
                    </small>
                )}
            </div>

            {stopMarkers.length > 0 && (
                <div className="stops-counter">
                    <div className="counter-circle">{stopMarkers.length}</div>
                    <span>Paradas agregadas</span>
                </div>
            )}

            <div className="wizard-undo-actions">
                <Button label="Deshacer" icon="pi pi-undo" className="p-button-outlined wizard-action-btn"
                        onClick={back} disabled={isReplayDisabled}/>
                <Button label="Restaurar" icon="pi pi-refresh" className="p-button-outlined wizard-action-btn"
                        onClick={restore} disabled={isRestoreDisabled}/>
                <Button label="Invertir orden" icon="pi pi-sort-alt" className="p-button-outlined wizard-action-btn"
                        onClick={reverseStops} disabled={stopMarkers.length < 2}/>
            </div>

            {stopMarkers.length > 0 && (
                <div className="stops-mini-list">
                    {markerDetails.map((detail, index) => (
                        <div key={index} className="stop-mini-item">
                            <div
                                className="stop-mini-number"
                                style={detail.paradaControl ? { backgroundColor: '#F59E0B' } : undefined}
                            >
                                {index + 1}
                            </div>
                            <div className="stop-mini-name">{detail.name}</div>
                            <Button
                                icon={detail.paradaControl ? 'pi pi-flag-fill' : 'pi pi-flag'}
                                className="p-button-rounded p-button-warning p-button-text"
                                onClick={() => toggleParadaControl(index)}
                                tooltip={detail.paradaControl ? 'Cambiar a parada normal' : 'Cambiar a parada de control'}
                                tooltipOptions={{ position: 'left' }}
                            />
                            <Button
                                icon="pi pi-trash"
                                className="p-button-rounded p-button-danger p-button-text stop-mini-delete"
                                onClick={() => removeStop(index)}
                                tooltip="Eliminar parada"
                                tooltipOptions={{ position: 'left' }}
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    // ---------------------------
    //  RENDER: Step 2 — Horarios
    //  Usa `availableEstructuras` → se adapta automáticamente al modo
    // ---------------------------
    const renderHorariosStep = () => {
        const updateHorarioTurno = (paradaIndex, estructuraId, horario) => {
            setMarkerDetails(prev => prev.map((detail, i) => {
                if (i !== paradaIndex) return detail;
                // Normalizar primero para que exista el horario de la estructura
                // aunque la parada no lo tuviera
                return {
                    ...detail,
                    horariosPorTurno: normalizarHorarios(detail.horariosPorTurno, availableEstructuras)
                        .map(h => h.estructuraId === estructuraId ? { ...h, horario } : h),
                };
            }));
        };

        const updateParadaInfo = (index, field, value) => {
            setMarkerDetails(prev => prev.map((detail, i) =>
                i === index ? { ...detail, [field]: value } : detail
            ));
        };

        return (
            <div className="wizard-step-content">
                <div className="wizard-instruction-card info">
                    <div className="instruction-icon"><i className="pi pi-clock" /></div>
                    <div className="instruction-text">
                        {isIndividual
                            ? `Configura el horario de cada parada para el turno: ${nombreTurnoConTipo(availableEstructuras[0])}.`
                            : 'Configura el horario de cada parada para todos los turnos.'
                        }
                    </div>
                </div>

                {isEntradaRoute && (
                    <div className="wizard-instruction-card" style={{ marginBottom: 12 }}>
                        <div className="instruction-icon"><i className="pi pi-info-circle" /></div>
                        <div className="instruction-text">
                            La última parada tiene pre-cargada la hora de llegada configurada en cada turno.
                            Verifica que esta hora sea correcta, ya que determina la hora de finalización del recorrido de los operadores.
                        </div>
                    </div>
                )}

                <ScrollPanel style={{ height: '400px' }}>
                    {markerDetails.map((parada, paradaIndex) => (
                        <Card key={paradaIndex} style={{ marginBottom: '16px' }}>
                            <h4 style={{ color: parada.paradaControl ? '#B45309' : '#6366f1' }}>
                                Parada {paradaIndex + 1}
                                {parada.paradaControl && (
                                    <span style={{ fontSize: '0.8rem', fontWeight: 600, marginLeft: 8 }}>
                                        <i className="pi pi-flag-fill" style={{ fontSize: '0.75rem', marginRight: 4 }} />
                                        De control
                                    </span>
                                )}
                            </h4>

                            <div className="wizard-field">
                                <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b' }}>Nombre</label>
                                <InputText
                                    value={parada.name}
                                    onChange={(e) => updateParadaInfo(paradaIndex, 'name', e.target.value)}
                                    className="input-stop"
                                    placeholder="Nombre de la parada"
                                    disabled={parada.paradaControl}
                                    tooltip={parada.paradaControl ? 'Las paradas de control tienen nombre fijo' : undefined}
                                />
                            </div>

                            <div className="wizard-field" style={{ marginBottom: '16px' }}>
                                <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b' }}>Referencia</label>
                                <InputText
                                    value={parada.reference}
                                    onChange={(e) => updateParadaInfo(paradaIndex, 'reference', e.target.value)}
                                    className="input-stop"
                                    placeholder="Punto de referencia"
                                />
                            </div>

                            <div>
                                <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b', marginBottom: '8px', display: 'block' }}>
                                    Horarios por turno
                                </label>

                                {/* ---- Iteramos SOLO availableEstructuras ---- */}
                                {availableEstructuras.map(estructura => {
                                    const horarioTurno = parada.horariosPorTurno
                                        .find(h => h.estructuraId === estructura.id);

                                    return (
                                        <div
                                            key={estructura.id}
                                            style={{
                                                display:         'flex',
                                                alignItems:      'center',
                                                marginBottom:    '8px',
                                                padding:         '8px',
                                                backgroundColor: '#f8fafc',
                                                borderRadius:    '6px',
                                                border:          '1px solid #e2e8f0',
                                            }}
                                        >
                                            <div style={{
                                                width:           '12px',
                                                height:          '12px',
                                                borderRadius:    '50%',
                                                backgroundColor: estructura.color,
                                                marginRight:     '8px',
                                            }} />
                                            <span style={{
                                                fontSize:  '0.85rem',
                                                fontWeight: 600,
                                                color:     '#1e293b',
                                                minWidth:  '80px',
                                            }}>
                                                {nombreTurnoConTipo(estructura)}:
                                            </span>
                                            <Calendar
                                                value={horarioTurno?.horario
                                                    ? new Date(`2000-01-01 ${horarioTurno.horario}`)
                                                    : null
                                                }
                                                onChange={(e) => {
                                                    if (!e.value) return;
                                                    const time = e.value.toLocaleTimeString('en-US', {
                                                        hour12:  false,
                                                        hour:    '2-digit',
                                                        minute:  '2-digit',
                                                    });
                                                    updateHorarioTurno(paradaIndex, estructura.id, time);                                                }}
                                                timeOnly
                                                placeholder="--:--"
                                                style={{ marginLeft: '8px', flex: 1 }}
                                                inputStyle={{ fontSize: '0.9rem', padding: '6px 8px' }}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </Card>
                    ))}
                </ScrollPanel>
            </div>
        );
    };

    // ---------------------------
    //  RENDER: Step 3 — Guardar
    // ---------------------------
    const saveBtnLabel = loading
        ? 'Guardando...'
        : guardado
            ? 'Guardado'
        : !isEditing
            ? 'Guardar ruta'
            : isIndividual
                ? 'Actualizar turno'
                : 'Actualizar ruta (todos los turnos)';

    const renderGuardarStep = () => (
        <div className="save-summary">
            <div className="wizard-instruction-card">
                <div className="instruction-icon"><i className="pi pi-check-circle" /></div>
                <div className="instruction-text">Revisa el resumen de tu ruta antes de guardar.</div>
            </div>

            {isIndividual && (
                <div className="wizard-instruction-card info" style={{ marginTop: 0 }}>
                    <div className="instruction-icon"><i className="pi pi-exclamation-triangle" style={{ color: '#F59E0B' }} /></div>
                    <div className="instruction-text" style={{ color: '#92400E' }}>
                        <strong>Modo individual:</strong> Solo se actualizará el turno <em>{nombreTurnoConTipo(availableEstructuras[0])}</em>.
                        Los demás trazos no serán afectados.
                    </div>
                </div>
            )}

            <div className="summary-card">
                <div className="summary-row">
                    <span className="summary-label">Cotización</span>
                    <span className="summary-value">{cotizacionId}</span>
                </div>
                <div className="summary-row">
                    <span className="summary-label">Cliente</span>
                    <span className="summary-value">{cotizacionData?.cliente.nombre}</span>
                </div>
                <div className="summary-row">
                    <span className="summary-label">Tipo de ruta</span>
                    <span className="summary-value">{tipoDisplay}</span>
                </div>
                <div className="summary-row">
                    <span className="summary-label">Nombre</span>
                    <span className="summary-value">{routeName}</span>
                </div>
                <div className="summary-row">
                    <span className="summary-label">Paradas</span>
                    <span className="summary-value">{stopMarkers.length}</span>
                </div>
                <div className="summary-row">
                    <span className="summary-label">Turnos afectados</span>
                    <span className="summary-value">{availableEstructuras.length}</span>
                </div>
                <div className="summary-row">
                    <span className="summary-label">Distancia</span>
                    <span className="summary-value">{summary?.totalDistance ?? '—'}</span>
                </div>
                <div className="summary-row">
                    <span className="summary-label">Tiempo estimado</span>
                    <span className="summary-value">{summary?.totalTime ?? '—'}</span>
                </div>
                <div className="summary-row">
                    <span className="summary-label">Color</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span className="summary-value">#{assignedColor}</span>
                        <span className="summary-color-dot" style={{ backgroundColor: `#${assignedColor}` }} />
                    </div>
                </div>
            </div>

            <Button
                label={saveBtnLabel}
                icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-save'}
                className="wizard-save-btn"
                onClick={saveRoute}
                disabled={loading || guardado}
                loading={loading}
            />
        </div>
    );

    // ---------------------------
    //  RENDER: despacho de pasos
    // ---------------------------
    const renderStepContent = () => {
        if (loadingCotizacion) {
            return (
                <div className="wizard-step-content">
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', color: '#6366f1' }} />
                        <p style={{ marginTop: '10px', color: '#64748b' }}>Cargando datos...</p>
                    </div>
                </div>
            );
        }

        switch (currentStep) {
            case 0: return renderInfoStep();
            case 1: return renderParadasStep();
            case 2: return renderHorariosStep();
            case 3: return renderGuardarStep();
            default: return null;
        }
    };

    // ---------------------------
    //  RENDER: pantalla de carga global
    // ---------------------------
    if (loadingCotizacion) {
        return (
            <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ textAlign: 'center' }}>
                    <i className="pi pi-spin pi-spinner" style={{ fontSize: '3rem', color: '#6366f1' }} />
                    <p style={{ marginTop: '16px', color: '#64748b' }}>
                        Cargando información de la cotización...
                    </p>
                </div>
            </div>
        );
    }

    // ---------------------------
    //  RENDER: layout principal
    // ---------------------------
    return (
        <>
            <Toast ref={toast} position="bottom-center" className="toast-color" />

            <div className="editor-layout">
                {/* ---- Panel Lateral ---- */}
                <aside className={`editor-sidebar ${panelCollapsed ? 'collapsed' : ''}`}>
                    {!panelCollapsed && (
                        <>
                            {/* Stepper */}
                            <div className="wizard-stepper">
                                {STEPS.map((step, idx) => (
                                    <button
                                        key={step.id}
                                        className={`stepper-item ${currentStep === idx ? 'active' : ''} ${currentStep > idx ? 'completed' : ''}`}
                                        onClick={() => {
                                            if (idx < currentStep || (idx === currentStep + 1 && canAdvanceToNextStep())) {
                                                setCurrentStep(idx);
                                            }
                                        }}
                                        aria-label={step.label}
                                        disabled={idx > currentStep + 1 || (idx === currentStep + 1 && !canAdvanceToNextStep())}
                                    >
                                        <span className="stepper-icon">
                                            {currentStep > idx
                                                ? <i className="pi pi-check" />
                                                : <i className={step.icon} />
                                            }
                                        </span>
                                        <span className="stepper-label">{step.label}</span>
                                    </button>
                                ))}
                            </div>

                            {/* Header */}
                            <div className="wizard-header">
                                <h3 className="wizard-title">{STEPS[currentStep].title}</h3>
                                <p className="wizard-hint">{getStepHint()}</p>
                            </div>

                            {/* Contenido */}
                            <ScrollPanel className="wizard-scroll">
                                {renderStepContent()}
                            </ScrollPanel>

                            {/* Navegación */}
                            <div className="wizard-nav">
                                {currentStep > 0 && (
                                    <Button
                                        label="Atrás"
                                        icon="pi pi-arrow-left"
                                        className="p-button-text wizard-nav-btn"
                                        onClick={handlePrevStep}
                                    />
                                )}
                                {currentStep < STEPS.length - 1 && (
                                    <Button
                                        label="Siguiente"
                                        icon="pi pi-arrow-right"
                                        iconPos="right"
                                        className="wizard-nav-btn wizard-nav-next"
                                        onClick={handleNextStep}
                                        disabled={!canAdvanceToNextStep()}
                                    />
                                )}
                            </div>
                        </>
                    )}
                </aside>

                {/* ---- Mapa ---- */}
                <main className="editor-map-area">
                    <div className="map-info-bar">
                        <div className="map-info-item">
                            <i className="pi pi-arrows-alt" />
                            <span>{summary?.totalDistance ?? '—'}</span>
                        </div>
                        <div className="map-info-item">
                            <i className="pi pi-clock" />
                            <span>{summary?.totalTime ?? '—'}</span>
                        </div>
                        <div className="map-info-item">
                            <i className="pi pi-map-marker" />
                            <span>{stopMarkers.length} paradas</span>
                        </div>
                    </div>

                    {currentStep === 1 && (
                        <div className="map-mode-indicator">
                            <i className="pi pi-map-marker" />
                            <span>Modo agregar paradas — Toca el mapa</span>
                        </div>
                    )}

                    {idaPolylineCoords.length > 0 && (
                        <div className="map-return-legend">
                            <div className="legend-item">
                                <span className="legend-line ghost" />
                                <span>{isReturnRoute ? 'Ruta de ida (referencia)' : 'Trazo anterior (referencia)'}</span>
                            </div>
                            <div className="legend-item">
                                <span className="legend-line active" style={{ backgroundColor: `#${assignedColor}` }} />
                                <span>Ruta nueva (editando)</span>
                            </div>
                        </div>
                    )}

                    <Map
                        center={[20.67129241646564, -103.35232101509537]}
                        zoom={13}
                        scrollWheelZoom
                        style={{ width: '100%', height: '100%' }}
                    >
                        {isReturnRoute && idaPolylineCoords.length > 0 && (
                            <Polyline
                                positions={idaPolylineCoords}
                                pathOptions={{ color: '#55687C', opacity: 0.45, weight: 4, dashArray: '10 6' }}
                            />
                        )}
                        <RoutingMachine
                            stops={stopMarkers}
                            onRouteFound={handleRouteFound}
                        />
                        <AddStops
                            addingStops={isAddingStop}
                            isEditing={isEditing}
                            setStopMarkers={handleAddStop}
                            stopMarkers={stopMarkers}
                        />
                        {stopMarkers.map((stop, idx) => (
                            <DraggableMarker
                                key={idx}
                                position={stop}
                                index={idx}
                                setStopMarkers={setStopMarkers}
                                isDraggable={isAddingStop}
                                onDragEnd={handleMarkerDrag}
                                esControl={!!markerDetails[idx]?.paradaControl}
                            />
                        ))}
                        {polyline.length > 0 && (
                            <Polyline
                                positions={polyline}
                                pathOptions={{ color: `#${assignedColor}`, opacity: 0.8, weight: 5 }}
                            />
                        )}
                    </Map>
                </main>
            </div>
        </>
    );
};

export default GenerarTrazoCotizacion;
